import { useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { useRfqReadScope } from "./useRfqReadScope";

type Drafts = {
  deadlineExtension: { deadlineAt: string; reason: string };
  unsealReason: string; announcementBody: string;
  answerDrafts: Record<string, { body: string; visibility: "ALL_INVITEES" | "PRIVATE" }>;
};
const initial = (): Drafts => ({ deadlineExtension: { deadlineAt: "", reason: "" }, unsealReason: "", announcementBody: "", answerDrafts: {} });
export function useRfqGovernanceDrafts(readScope: ReturnType<typeof useRfqReadScope>) {
  const [state, setState] = useState(() => ({ readScope, ...initial() }));
  const revisions = useRef({ deadlineExtension: 0, unsealReason: 0, announcementBody: 0, answerDrafts: 0 });
  const visible = state.readScope === readScope ? state : { readScope, ...initial() };
  const setDraft = <K extends keyof Drafts>(key: K, value: SetStateAction<Drafts[K]>) => {
    if (!readScope.isCurrent()) return;
    revisions.current[key]++;
    setState(current => {
      const previous = current.readScope === readScope ? current : { readScope, ...initial() };
      return { ...previous, [key]: typeof value === "function" ? value(previous[key]) : value };
    });
  };
  const setDeadlineExtension: Dispatch<SetStateAction<Drafts["deadlineExtension"]>> = value => setDraft("deadlineExtension", value);
  const setUnsealReason: Dispatch<SetStateAction<string>> = value => setDraft("unsealReason", value);
  const setAnnouncementBody: Dispatch<SetStateAction<string>> = value => setDraft("announcementBody", value);
  const setAnswerDrafts: Dispatch<SetStateAction<Drafts["answerDrafts"]>> = value => setDraft("answerDrafts", value);
  const clearIfUnchanged = (key: "deadlineExtension" | "unsealReason" | "announcementBody", revision: number) => {
    if (!readScope.isCurrent() || revisions.current[key] !== revision) return;
    setState(current => ({ ...current, [key]: initial()[key] }));
  };
  const clearAnswerIfUnchanged = (questionId: string, draft: Drafts["answerDrafts"][string]) => {
    if (!readScope.isCurrent()) return;
    setState(current => {
      if (current.readScope !== readScope || current.answerDrafts[questionId] !== draft) return current;
      const next = { ...current.answerDrafts };
      delete next[questionId];
      return { ...current, answerDrafts: next };
    });
  };
  return { ...visible, revisions, renderedRevisions: { ...revisions.current }, setDeadlineExtension, setUnsealReason, setAnnouncementBody, setAnswerDrafts, clearIfUnchanged, clearAnswerIfUnchanged };
}
